import React, { useState, useEffect } from 'react';
import { Cookie, Check, X } from 'lucide-react';

// Extend the Window interface for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('verifiko_cookie_consent');
    
    // Restore consent state from localStorage if available
    if (consent === 'true') {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'analytics_storage': 'granted'
        });
      }
    } else if (consent === 'false') {
      // Ensure it stays denied if explicitly rejected (redundant if default is denied, but good for clarity)
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'analytics_storage': 'denied'
        });
      }
    }

    if (consent === null) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('verifiko_cookie_consent', 'true');
    
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted'
      });
    }
    
    setIsVisible(false);
  };

  const denyCookies = () => {
    localStorage.setItem('verifiko_cookie_consent', 'false');
    
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied'
      });
    }
    
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:bottom-4 md:max-w-xl animate-[slide-in-from-bottom_0.7s_ease-out] fade-in">
      <div className="relative flex flex-col gap-4 rounded-xl border border-white/10 bg-primary/95 p-6 shadow-[0_0_40px_-10px_rgba(0,0,0,0.5)] backdrop-blur-md">
        
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-accent/10 p-2 text-accent shrink-0 mt-1">
            <Cookie className="h-6 w-6" />
          </div>
          <div className="space-y-4">
            <h3 className="font-headline font-bold text-white">Dbamy o Twoją prywatność</h3>
            
            <div className="space-y-3 text-sm text-slate-400 leading-relaxed">
              {/* Item 1 - Accepted/Necessary */}
              <div className="flex gap-3 items-start">
                <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <span>
                  Używamy niezbędnych plików cookie, aby zapewnić najlepszą jakość korzystania z naszej strony. Kontynuując przeglądanie, zgadzasz się na ich wykorzystanie.
                </span>
              </div>

              {/* Item 2 - Marketing Rejected */}
              <div className="flex gap-3 items-start">
                <X className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <span>
                  Nie zbieramy danych na potrzeby marketingu.
                </span>
              </div>

              {/* Item 3 - Question/Stats */}
              <div className="flex gap-3 items-start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-6 w-6 text-accent shrink-0"
                >
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <path d="M12 17h.01" />
                </svg>
                <span className="font-medium text-slate-200">
                  Statystyczne pliki cookie pomagają nam zrozumieć, jak odwiedzający wchodzą w interakcję ze stroną, poprzez anonimowe zbieranie i raportowanie informacji - Zaakcetpuj je lub Odrzuć.
                </span>
              </div>
            </div>

          </div>
        </div>

        <div className="flex gap-3 pt-2 justify-end">
          <button
            onClick={acceptCookies}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-bold transition-all bg-accent text-primary hover:bg-accent-hover h-10 px-6 w-32 cursor-pointer shadow-[0_0_15px_rgba(0,225,173,0.2)] hover:shadow-[0_0_20px_rgba(0,225,173,0.4)]"
          >
            Akceptuję
          </button>
          <button
            onClick={denyCookies}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-white/20 bg-transparent text-white hover:bg-white/10 h-10 px-6 w-32"
          >
            Odrzuć
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;